import { Box } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { SelectElement } from 'react-hook-form-mui';
import { FormattedMessage } from 'react-intl';
import type { ReportDefinition, ReportDefinitionData } from '../../../types';
import {
  DEFAULT_CATEGORY_ID,
  type BlueprintColumn,
  type Language,
  type LocalizedLabel,
} from '../../../types';

function resolveLabel(
  label: LocalizedLabel | undefined,
  language: Language,
  fallback: string,
): string {
  if (!label) return fallback;
  return label[language] || fallback;
}

interface SegmentationColumnFieldProps {
  name: `data.sections.${number}.segmentation.columnId`;
  sectionIndex: number;
  columns: BlueprintColumn[];
  primaryLanguage: Language;
}

/**
 * Dropdown to select which column to use for segmentation, with helper text about XLS/CSV behavior.
 * Uses react-hook-form-mui SelectElement bound to the form.
 */
export function SegmentationColumnField({
  name,
  sectionIndex,
  columns,
  primaryLanguage,
}: SegmentationColumnFieldProps) {
  const { watch } = useFormContext<ReportDefinition>();
  const sections = watch('data.sections') as ReportDefinitionData['sections'] | undefined;
  const formColumns = sections?.[sectionIndex]?.columns ?? [];

  const labelById = new Map(formColumns.map((c) => [c.id, c.label as LocalizedLabel]));

  const options = columns.map((col) => {
    const userLabel = labelById.get(col.id);
    const columnLabel = resolveLabel(
      userLabel ?? (col.defaultLabel as LocalizedLabel),
      primaryLanguage,
      col.key,
    );
    const categoryText =
      (col.category || DEFAULT_CATEGORY_ID) !== DEFAULT_CATEGORY_ID ? ` (${col.category})` : '';
    return { id: col.id, label: `${columnLabel}${categoryText}` };
  });

  return (
    <Box sx={{ mb: 1 }}>
      <SelectElement<ReportDefinition>
        name={name}
        label={
          <FormattedMessage
            id="audit.reportDefinitions.segmentation.groupBy"
            defaultMessage="Columna"
          />
        }
        options={options}
        size="small"
        fullWidth
        helperText={
          <FormattedMessage
            id="audit.reportDefinitions.segmentation.helperText"
            defaultMessage="En archivos XLS se generará una hoja por cada valor del campo seleccionado. En archivos CSV se generará un archivo distinto dentro de un .zip"
          />
        }
      />
    </Box>
  );
}
